/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Activity, Cpu, Users, Store, Bot, QrCode, Smartphone, Blocks, CheckCircle, Server, ArrowDown } from 'lucide-react';

// --- TRANSACTION VALIDATION DIAGRAM ---
export const TransactionValidationDiagram: React.FC = () => {
  const steps = [
    { name: "1. POS Initiates", desc: "Merchant creates a charge.", icon: Store },
    { name: "2. Customer Scans", desc: "Wallet reads QR code.", icon: QrCode },
    { name: "3. User Confirms", desc: "Signs transaction.", icon: Smartphone },
    { name: "4. On-Chain Tx", desc: "Payment broadcasted to network.", icon: Blocks },
    { name: "5. Gateway Validates", desc: "GhostOS correlates data.", icon: Server },
    { name: "6. POS Confirms", desc: "Receives success signal.", icon: CheckCircle },
  ];

  return (
    <div className="p-6 md:p-8 bg-white dark:bg-[#1A1D23] rounded-xl shadow-sm border border-stone-200 dark:border-white/10 my-8">
         <h3 className="font-serif text-xl mb-4 text-stone-800 dark:text-white text-center">Transaction Flow</h3>
         <p className="text-sm text-stone-500 dark:text-stone-400 mb-8 text-center max-w-md mx-auto">
            An overview of how a payment is processed from initiation to final on-chain confirmation and validation.
        </p>
        <div className="space-y-4">
            {steps.map((step, i) => (
                <React.Fragment key={step.name}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.15 }}
                        className="flex items-center gap-4 p-4 bg-stone-50/50 dark:bg-white/5 rounded-lg border border-stone-200 dark:border-white/10"
                    >
                        <div className="w-10 h-10 rounded-full bg-stone-100 dark:bg-white/10 border-2 border-stone-200 dark:border-white/20 flex items-center justify-center text-stone-600 dark:text-stone-300 shrink-0">
                            <step.icon size={20} />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-bold text-sm text-stone-800 dark:text-white">{step.name}</h4>
                            <p className="text-xs text-stone-500">{step.desc}</p>
                        </div>
                    </motion.div>
                    {i < steps.length - 1 && (
                        <div className="flex justify-center">
                            <ArrowDown size={16} className="text-stone-300 dark:text-stone-700"/>
                        </div>
                    )}
                </React.Fragment>
            ))}
        </div>
    </div>
  );
};

// --- GHOST OS ARCHITECTURE DIAGRAM ---
export const GhostOSArchitectureDiagram: React.FC = () => {
    const actorClasses = "bg-white dark:bg-[#1A1D23] p-6 rounded-xl border border-stone-200 dark:border-white/10 flex flex-col items-center gap-3 text-center shadow-sm";
    return (
        <div className="p-6 md:p-8 bg-[#F5F4F0] dark:bg-[#0A0B0F] rounded-xl border border-stone-200 dark:border-white/10 my-8">
            <h3 className="font-serif text-xl mb-4 text-stone-900 dark:text-white text-center">GhostOS Architecture</h3>
            <p className="text-sm text-stone-600 dark:text-stone-400 mb-8 text-center max-w-md mx-auto">
                The OS processes on-chain events and off-chain data through a secure runtime, correlating information to validate transactions and update state.
            </p>

            <div className="flex flex-col items-center gap-6">
                {/* Inputs */}
                <div className="grid grid-cols-3 gap-4 w-full max-w-2xl">
                    <div className={actorClasses}>
                        <div className="w-12 h-12 bg-stone-100 dark:bg-white/5 rounded-full flex items-center justify-center text-stone-500"><Users size={24} /></div>
                        <span className="text-xs font-bold text-stone-700 dark:text-stone-300">People</span>
                    </div>
                    <div className={actorClasses}>
                        <div className="w-12 h-12 bg-stone-100 dark:bg-white/5 rounded-full flex items-center justify-center text-stone-500"><Store size={24} /></div>
                        <span className="text-xs font-bold text-stone-700 dark:text-stone-300">Merchants</span>
                    </div>
                     <div className={actorClasses}>
                        <div className="w-12 h-12 bg-stone-100 dark:bg-white/5 rounded-full flex items-center justify-center text-stone-500"><Bot size={24} /></div>
                        <span className="text-xs font-bold text-stone-700 dark:text-stone-300">AI Agents</span>
                    </div>
                </div>

                {/* Connectors */}
                <div className="flex justify-center h-8 w-px bg-stone-300 dark:bg-white/20 relative">
                     <div className="absolute bottom-0 text-stone-300 dark:text-white/20">↓</div>
                </div>

                {/* Core Runtime */}
                <div className="bg-stone-900 text-white p-8 rounded-xl border-2 border-stone-800 dark:border-white/20 w-full max-w-xs text-center relative overflow-hidden">
                    <motion.div 
                       className="absolute inset-0 bg-gradient-to-t from-nobel-gold/20 to-transparent"
                       animate={{ y: ['-100%', '100%']}}
                       transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                    />
                    <div className="relative z-10 flex flex-col items-center gap-3">
                       <Cpu size={32} className="text-nobel-gold"/>
                       <h4 className="font-bold text-lg">GhostOS Runtime</h4>
                       <p className="text-xs text-stone-400">Secure validation & state management</p>
                    </div>
                </div>

                 {/* Connector */}
                <div className="flex justify-center h-8 w-px bg-stone-300 dark:bg-white/20 relative">
                     <div className="absolute bottom-0 text-stone-300 dark:text-white/20">↓</div>
                </div>
                
                {/* Output */}
                <div className="bg-white dark:bg-[#1A1D23] p-6 rounded-xl border border-stone-200 dark:border-white/10 flex items-center gap-4 shadow-sm">
                   <div className="w-12 h-12 bg-green-100 dark:bg-green-500/10 rounded-full flex items-center justify-center text-green-600 dark:text-green-400">
                       <CheckCircle size={24}/>
                   </div>
                   <div>
                       <h4 className="font-bold text-stone-800 dark:text-white">Validated On-Chain State</h4>
                       <p className="text-xs text-stone-500 dark:text-stone-400">Immutable & Auditable</p>
                   </div>
                </div>
            </div>
        </div>
    );
};